import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {action, makeAutoObservable, observable} from "mobx";
import {Bindings} from "data/constants/bindings";
import type {IUser, IUserStore, IUpdateUserData} from "data/stores/user/user.store";
import React from "react";
import {AxiosError} from "axios";
import {IAxiosApiError} from "data/types/api";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IProfile} from "data/providers/api/auth.api.provider";

interface IDataForm extends HTMLFormElement {
	isNotificationsEnabled: HTMLInputElement;
}

export interface IProfileController extends ViewController {
	isFormChanged: boolean;
	isRequest: boolean;
	isFormUpdated: boolean;

	get user(): IUser | undefined;
	get profile(): IProfile | undefined;

	onChangeNotificationStatus: () => void;
	logout: () => void;
	handleForm: (event: React.SyntheticEvent<IDataForm>) => void;
}

@injectable()
export class ProfileController implements IProfileController {
	@observable _isNotificationsEnabled: boolean = Boolean(
		this._userStore.user?.isNotificationsEnabled
	);
	@observable public isFormChanged: boolean = false;
	@observable public isRequest: boolean = false;
	@observable public isFormUpdated: boolean = false;

	get user() {
		return this._userStore.user;
	}

	get profile() {
		return this._userStore.profile;
	}

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	dispose(): void {
		return;
	}

	init(): void {
		return;
	}

	public onChangeNotificationStatus = () => {
		this.isFormUpdated = false;
		this._isNotificationsEnabled = !this._isNotificationsEnabled;
		this.isFormChanged =
			this._isNotificationsEnabled !== Boolean(this._userStore.user?.isNotificationsEnabled);
	};

	public logout = () => {
		void this._userStore.logout();
	};

	@action handleForm = (event: React.SyntheticEvent<IDataForm>) => {
		event.preventDefault();

		if (this.isRequest) {
			return;
		}

		const {isNotificationsEnabled} = event.currentTarget;
		const date: IUpdateUserData = {
			isNotificationsEnabled: isNotificationsEnabled.checked,
		};

		this.isRequest = true;

		this._userStore
			.update(date)
			.catch((e: unknown) => {
				const error = e as AxiosError<IAxiosApiError, unknown>;
				this._modalsStore.showAxiosError(error);
			})
			.finally(() => {
				this.isRequest = false;
				this.isFormChanged = false;
				this.isFormUpdated = true;
			});
	};
}
